import type { SearchRankingFactor, SearchResponse, SearchResult } from '@powerful/data';
import { resolveLocale } from '@powerful/config/i18n';
import { useTranslation } from 'react-i18next';

import { Icon } from '../icons/icon';
import { OverlayDialog } from './overlay-dialog';

/**
 * Transparência do ranking — canvas `RankingHow` / `RankingWhy` (🔴 C7, P2B art. 5.º).
 *
 * Tudo o que aparece vem da resposta real da Edge: pesos (`weights`), fatores por resultado e a
 * influência do plano. Nenhum número é escrito no texto. `sponsored:false` é invariante do MVP —
 * por isso não existe rótulo «Patrocinado» em lado nenhum.
 */
export type RankingDialogState =
  | { mode: 'why'; result: SearchResult }
  | { mode: 'how' }
  | null;

export interface RankingDialogProps {
  state: RankingDialogState;
  weights: SearchResponse['weights'] | null;
  onClose: () => void;
  onShowHow: () => void;
}

const FACTORS: readonly SearchRankingFactor['key'][] = ['distance', 'rating', 'plan', 'recency'];

export function RankingDialog({ state, weights, onClose, onShowHow }: RankingDialogProps) {
  const { t, i18n } = useTranslation();
  const locale = resolveLocale(i18n.language);
  const decimal = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const percent = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 });
  const clamp = (v: number) => Math.min(1, Math.max(0, v));

  if (!state) return null;

  if (state.mode === 'why') {
    const { business, ranking } = state.result;
    const byKey = new Map(ranking.factors.map((f) => [f.key, f]));
    return (
      <OverlayDialog
        open
        onClose={onClose}
        title={t('discovery.ranking.whyTitle')}
        description={t('discovery.ranking.whyIntro', { name: business.name })}
        testId="ranking-why"
        footer={<button className="btn btn-ghost" type="button" onClick={onClose}>{t('actions.close')}</button>}
      >
        <div className="pc-sec gap-4">
          {FACTORS.filter((key) => byKey.has(key)).map((key) => {
            const f = byKey.get(key)!;
            return (
              <div className="pc-factor" key={key}>
                <span className="nm">{t(`discovery.ranking.factors.${key}.name`)}</span>
                <span className="w">{decimal.format(clamp(f.value))}</span>
                <div className="pc-bar" aria-hidden="true"><i style={{ width: `${Math.round(clamp(f.value) * 100)}%` }} /></div>
              </div>
            );
          })}
        </div>
        {ranking.commercial.planInfluenced ? (
          <div className="pc-note" role="note">
            <Icon name="tag" size={18} />
            <span>{t('discovery.ranking.planInfluenced')}</span>
          </div>
        ) : null}
        <button className="hit pc-textbtn self-start" type="button" onClick={onShowHow}>
          <Icon name="info" size={16} />
          <span>{t('discovery.ranking.how')}</span>
        </button>
      </OverlayDialog>
    );
  }

  return (
    <OverlayDialog
      open
      onClose={onClose}
      title={t('discovery.ranking.howTitle')}
      description={t('discovery.ranking.howIntro')}
      testId="ranking-how"
      footer={<button className="btn btn-ghost" type="button" onClick={onClose}>{t('actions.close')}</button>}
    >
      {weights ? (
        <div className="pc-sec gap-4">
          {FACTORS.map((key) => (
            <div className="pc-factor" key={key}>
              <span className="nm">{t(`discovery.ranking.factors.${key}.name`)}</span>
              <span className="w">{percent.format(clamp(weights[key]))}</span>
              <div className="pc-bar" aria-hidden="true"><i style={{ width: `${Math.round(clamp(weights[key]) * 100)}%` }} /></div>
              <span className="ds">{t(`discovery.ranking.factors.${key}.desc`)}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className="pc-muted">{t('discovery.ranking.weightsPending')}</p>
      )}
      <div className="pc-note" role="note">
        <Icon name="info" size={18} />
        <span><strong className="text-ink-1">{t('discovery.ranking.planNote')}</strong> {t('discovery.ranking.planOnly')}</span>
      </div>
      <div className="pc-sec">
        <h3 className="pc-h3">{t('discovery.ranking.notTitle')}</h3>
        <ul className="pc-ul">
          {(['paid', 'history', 'seal'] as const).map((key) => (
            <li key={key}><Icon name="close" size={16} /><span>{t(`discovery.ranking.not.${key}`)}</span></li>
          ))}
        </ul>
      </div>
      {weights ? <p className="pc-muted">{t('discovery.ranking.version', { version: weights.version })}</p> : null}
    </OverlayDialog>
  );
}
